/**
 * useProjects — TanStack Query hooks for the projects feature.
 *
 * Server collection lives here (NOT in Zustand) so we get:
 *   - request dedup across tabs that mount the pill simultaneously,
 *   - 5min ``staleTime`` matched to the typical project edit cadence,
 *   - first-class ``invalidateQueries`` after mutations.
 *
 * The selected-id slice lives in ``projectsStore`` — see
 * ``useCurrentProject`` for the resolution rule.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  type CreateProjectFromTemplateInput,
  type CreateProjectInput,
  type CreateProjectTaskInput,
  type InviteProjectMemberInput,
  type Project,
  type ProjectArtifact,
  type ProjectAutomationSummary,
  type ProjectMember,
  type ProjectMemberRole,
  type ProjectMemoryEntry,
  type ProjectTask,
  type ProjectTemplate,
  type UpdateProjectInput,
  type UpdateProjectTaskInput,
  archiveProject,
  assignConversationToProject,
  createProject,
  createProjectFromTemplate,
  createProjectTask,
  deleteProjectTask,
  inviteProjectMember,
  listProjectArtifacts,
  listProjectAutomations,
  listProjectMembers,
  listProjectMemoryTimeline,
  listProjectTasks,
  listProjectTemplates,
  listProjects,
  removeProjectMember,
  reorderProjects,
  unassignConversationFromProject,
  updateProject,
  updateProjectMemberRole,
  updateProjectTask,
} from "@ando/core/api";

import { selectCurrentProjectId, useProjectsStore } from "../state/projectsStore.js";

/** Canonical query key — exported so external slices can invalidate. */
export const projectsQueryKey = ["projects"] as const;

/** Conversation sidebar key — kept in sync with ``useConversationsQuery``. */
const CONVERSATIONS_KEY_ROOT = ["chat", "conversations"] as const;

const PROJECTS_STALE_TIME_MS = 5 * 60_000;

/**
 * Shared query definition for the projects list. Consumed by BOTH the
 * ``useProjects`` hook and the ``/workspace/projects`` route loader
 * (``ensureQueryData``) so hover/focus intent-preload warms the SAME cache
 * entry the component reads — the tab paints from cache instead of spinning.
 */
export function projectsQueryOptions() {
  return queryOptions({
    queryKey: projectsQueryKey,
    queryFn: listProjects,
    staleTime: PROJECTS_STALE_TIME_MS,
  });
}

/** GET ``/projects`` — list non-archived projects, ordered by ``position``. */
export function useProjects(): UseQueryResult<readonly Project[]> {
  return useQuery(projectsQueryOptions());
}

/**
 * Resolve the current project from (selected id, server list).
 *
 * Returns ``null`` in three legitimate cases:
 *   1. ``currentId === null`` — user has explicitly chosen "Tutti" /
 *      no-project (May 22, 2026). The pill renders the generic label
 *      and new chats land in the "Senza progetto" bucket.
 *   2. The selected id doesn't resolve in the server list (project
 *      archived or list still hydrating).
 *   3. The list is empty / loading.
 *
 * Earlier versions auto-fell-back to ``data[0]`` to mimic "Generale",
 * but that made the no-project state unreachable from the UI — the
 * dropdown couldn't surface a "Tutti" entry because every render
 * silently re-selected a project. ``null`` is now a first-class state.
 */
export function useCurrentProject(): Project | null {
  const { data } = useProjects();
  const currentId = useProjectsStore(selectCurrentProjectId);
  if (currentId === null || !data || data.length === 0) return null;
  return data.find((p) => p.project_id === currentId) ?? null;
}

export function useCreateProject(): UseMutationResult<Project, Error, CreateProjectInput> {
  const qc = useQueryClient();
  return useMutation<Project, Error, CreateProjectInput>({
    mutationFn: createProject,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsQueryKey });
    },
  });
}

export function useUpdateProject(): UseMutationResult<
  Project,
  Error,
  { projectId: string; patch: UpdateProjectInput }
> {
  const qc = useQueryClient();
  return useMutation<Project, Error, { projectId: string; patch: UpdateProjectInput }>({
    mutationFn: ({ projectId, patch }) => updateProject(projectId, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsQueryKey });
    },
  });
}

export function useArchiveProject(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: archiveProject,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsQueryKey });
      // Conversations may have lost their project_id link — refetch sidebar.
      void qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY_ROOT });
    },
  });
}

export function useReorderProjects(): UseMutationResult<void, Error, readonly string[]> {
  const qc = useQueryClient();
  return useMutation<void, Error, readonly string[]>({
    mutationFn: reorderProjects,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsQueryKey });
    },
  });
}

export function useAssignConversationToProject(): UseMutationResult<
  void,
  Error,
  { projectId: string; conversationId: string }
> {
  const qc = useQueryClient();
  return useMutation<void, Error, { projectId: string; conversationId: string }>({
    mutationFn: ({ projectId, conversationId }) =>
      assignConversationToProject(projectId, conversationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsQueryKey });
      void qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY_ROOT });
    },
  });
}

export function useUnassignConversationFromProject(): UseMutationResult<
  void,
  Error,
  { projectId: string; conversationId: string }
> {
  const qc = useQueryClient();
  return useMutation<void, Error, { projectId: string; conversationId: string }>({
    mutationFn: ({ projectId, conversationId }) =>
      unassignConversationFromProject(projectId, conversationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsQueryKey });
      void qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY_ROOT });
    },
  });
}

// ---------------------------------------------------------------------------
// Phase 1 sub-resources (June 7, 2026) — per-project artifacts + tasks
// ---------------------------------------------------------------------------

/** Stable key roots — exported so sibling features can invalidate. */
export const projectArtifactsQueryKey = (projectId: string) =>
  ["projects", projectId, "artifacts"] as const;

export const projectTasksQueryKey = (projectId: string) =>
  ["projects", projectId, "tasks"] as const;

/**
 * GET ``/projects/{id}/artifacts`` — newest-first artifact gallery feed.
 *
 * 60s ``staleTime`` — artifacts only land via streaming AI work, not
 * client edits, so a slightly longer window keeps the gallery quiet
 * while still picking up new forks within a minute. The query is
 * disabled when ``projectId`` is empty so callers can mount the hook
 * conditionally without an ``enabled`` flag.
 */
export function useProjectArtifacts(
  projectId: string,
  limit = 100,
): UseQueryResult<readonly ProjectArtifact[]> {
  return useQuery<readonly ProjectArtifact[]>({
    queryKey: projectArtifactsQueryKey(projectId),
    queryFn: () => listProjectArtifacts(projectId, limit),
    enabled: projectId.length > 0,
    staleTime: 60_000,
  });
}

/**
 * GET ``/projects/{id}/tasks`` — incomplete-first task feed.
 *
 * 30s ``staleTime`` — tasks flip state interactively, but the FE
 * mutation hooks below invalidate eagerly so the perceived freshness
 * comes from optimistic invalidation, not poll.
 */
export function useProjectTasks(
  projectId: string,
  options?: { includeCompleted?: boolean; limit?: number },
): UseQueryResult<readonly ProjectTask[]> {
  return useQuery<readonly ProjectTask[]>({
    queryKey: [
      ...projectTasksQueryKey(projectId),
      options?.includeCompleted ?? true,
    ] as const,
    queryFn: () => listProjectTasks(projectId, options),
    enabled: projectId.length > 0,
    staleTime: 30_000,
  });
}

export function useCreateProjectTask(
  projectId: string,
): UseMutationResult<ProjectTask, Error, CreateProjectTaskInput> {
  const qc = useQueryClient();
  return useMutation<ProjectTask, Error, CreateProjectTaskInput>({
    mutationFn: (input) => createProjectTask(projectId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectTasksQueryKey(projectId) });
    },
  });
}

export function useUpdateProjectTask(
  projectId: string,
): UseMutationResult<
  ProjectTask,
  Error,
  { taskId: string; patch: UpdateProjectTaskInput }
> {
  const qc = useQueryClient();
  return useMutation<
    ProjectTask,
    Error,
    { taskId: string; patch: UpdateProjectTaskInput }
  >({
    mutationFn: ({ taskId, patch }) => updateProjectTask(projectId, taskId, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectTasksQueryKey(projectId) });
    },
  });
}

export function useDeleteProjectTask(
  projectId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (taskId) => deleteProjectTask(projectId, taskId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectTasksQueryKey(projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Phase 2 — HyperMem per-project memory timeline (June 8, 2026)
// ---------------------------------------------------------------------------

export const projectMemoryQueryKey = (projectId: string) =>
  ["projects", projectId, "memory", "timeline"] as const;

/**
 * GET ``/projects/{id}/memory/timeline`` — newest-first project memory.
 *
 * 90s ``staleTime`` because new episodic facts only land via the
 * Temporal HyperMem pipeline at the end of a turn — clients don't
 * mutate this list, and refetching too eagerly would burn the BE for
 * no perceptible UX gain. The Memoria section refetches naturally on
 * focus thanks to TanStack's default.
 */
export function useProjectMemoryTimeline(
  projectId: string,
  limit = 50,
): UseQueryResult<readonly ProjectMemoryEntry[]> {
  return useQuery<readonly ProjectMemoryEntry[]>({
    queryKey: projectMemoryQueryKey(projectId),
    queryFn: () => listProjectMemoryTimeline(projectId, limit),
    enabled: projectId.length > 0,
    staleTime: 90_000,
  });
}

// ---------------------------------------------------------------------------
// Phase 3 — per-project automations (June 9, 2026)
// ---------------------------------------------------------------------------

export const projectAutomationsQueryKey = (projectId: string) =>
  ["projects", projectId, "automations"] as const;

/**
 * GET ``/projects/{id}/automations`` — Hive Flows pinned to this project.
 *
 * 30s ``staleTime`` matches the workspace ``AutomationsHub`` cadence:
 * flow state flips on activation / pause and the user expects the
 * indicator to follow quickly. Cheap to refetch (compact projection,
 * no graph).
 */
export function useProjectAutomations(
  projectId: string,
  limit = 50,
): UseQueryResult<readonly ProjectAutomationSummary[]> {
  return useQuery<readonly ProjectAutomationSummary[]>({
    queryKey: projectAutomationsQueryKey(projectId),
    queryFn: () => listProjectAutomations(projectId, limit),
    enabled: projectId.length > 0,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Phase 4 — vertical starter packs (June 10, 2026)
// ---------------------------------------------------------------------------

export const projectTemplatesQueryKey = (vertical?: string) =>
  vertical
    ? (["project-templates", vertical] as const)
    : (["project-templates", "all"] as const);

/**
 * GET ``/project-templates`` — curated catalog.
 *
 * 10min ``staleTime`` because the catalog is updated via deploy, not
 * by the user — the browser stays snappy without re-fetching on every
 * mount of the TemplatesBrowser dialog.
 */
export function useProjectTemplates(
  vertical?: string,
): UseQueryResult<readonly ProjectTemplate[]> {
  return useQuery<readonly ProjectTemplate[]>({
    queryKey: projectTemplatesQueryKey(vertical),
    queryFn: () => listProjectTemplates(vertical),
    staleTime: 10 * 60_000,
  });
}

/**
 * POST ``/projects/from-template`` — clone a starter pack. On success,
 * the new project is selected as ``currentProjectId`` so the breadcrumb
 * pill follows the user to the Detail page.
 */
export function useCreateProjectFromTemplate(): UseMutationResult<
  Project,
  Error,
  CreateProjectFromTemplateInput
> {
  const qc = useQueryClient();
  const setCurrentProjectId = useProjectsStore(
    (s) => s.setCurrentProjectId,
  );
  return useMutation<Project, Error, CreateProjectFromTemplateInput>({
    mutationFn: createProjectFromTemplate,
    onSuccess: (created) => {
      setCurrentProjectId(created.project_id);
      void qc.invalidateQueries({ queryKey: projectsQueryKey });
    },
  });
}

// ---------------------------------------------------------------------------
// Phase 5a — intra-org members (June 13, 2026)
// ---------------------------------------------------------------------------

export const projectMembersQueryKey = (projectId: string) =>
  ["projects", projectId, "members"] as const;

/**
 * GET ``/projects/{id}/members`` — owner-first list of members.
 *
 * 60s ``staleTime`` because membership only changes via the invite /
 * role-flip / remove mutations defined below, which already
 * invalidate this key.
 */
export function useProjectMembers(
  projectId: string,
): UseQueryResult<readonly ProjectMember[]> {
  return useQuery<readonly ProjectMember[]>({
    queryKey: projectMembersQueryKey(projectId),
    queryFn: () => listProjectMembers(projectId),
    enabled: projectId.length > 0,
    staleTime: 60_000,
  });
}

export function useInviteProjectMember(
  projectId: string,
): UseMutationResult<ProjectMember, Error, InviteProjectMemberInput> {
  const qc = useQueryClient();
  return useMutation<ProjectMember, Error, InviteProjectMemberInput>({
    mutationFn: (input) => inviteProjectMember(projectId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectMembersQueryKey(projectId) });
    },
  });
}

export function useUpdateProjectMemberRole(
  projectId: string,
): UseMutationResult<
  ProjectMember,
  Error,
  { memberUserId: string; role: Exclude<ProjectMemberRole, "owner"> }
> {
  const qc = useQueryClient();
  return useMutation<
    ProjectMember,
    Error,
    { memberUserId: string; role: Exclude<ProjectMemberRole, "owner"> }
  >({
    mutationFn: ({ memberUserId, role }) =>
      updateProjectMemberRole(projectId, memberUserId, role),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectMembersQueryKey(projectId) });
    },
  });
}

export function useRemoveProjectMember(
  projectId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (memberUserId) => removeProjectMember(projectId, memberUserId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectMembersQueryKey(projectId) });
      // Self-leave also flips the projects list (the user no longer
      // sees this project) — invalidate the global list too.
      void qc.invalidateQueries({ queryKey: projectsQueryKey });
    },
  });
}
